import { useCallback, useEffect, useRef, useState } from "react";
import VideoService from "../service/VideoService";
import VormatsAPI from "../../common/apis/VormatsAPI";
import JSZip from "jszip";
import { ToastNotification } from "../components/Toast/Toast";
import { useTranslation } from "react-i18next";

const useDownloadVideo = ({ video, subtitlesLanguage = {}, burnCta = false, socialsFriendly = false, callback = null }) => {
    const [percentage, setPercentage] = useState(0);
    const [status, setStatus] = useState('pending');
    const [downloadLink, setDownloadLink] = useState(null);
    const intervalRef = useRef(null);
    const [estimatedTime, setEstimatedTime] = useState(0);
    const { t } = useTranslation();

    let timeElapsed = 0;

    const updatePercentage = () => {
        timeElapsed = timeElapsed + 1;
        let newPercentage = Math.min(100, Math.round((timeElapsed / estimatedTime) * 100));

        if (newPercentage >= 100) {
            clearInterval(intervalRef.current);
            if (status === 'downloading') {
                setPercentage(100);
                clearInterval(intervalRef.current);
            } else {
                setPercentage(99); // Only set percentage to 100 if we're fully ready
            }
        } else {
            setPercentage(newPercentage);
        }
    };

    const onError = async () => {
        clearInterval(intervalRef.current);
        setStatus('error');
    };

    const onSuccess = async ({ downloadLink }, isDownloadingAllResources) => {
        clearInterval(intervalRef.current);
        setPercentage(100);
        if (isDownloadingAllResources && downloadLink) {
            await handleDownloadMetadata(true);
        } else {
            setStatus('downloading');
            setDownloadLink(downloadLink);
        }
    };


    const onStart = async (isDownloadingAllResources = false) => {
        const params = {
            incl_subs: Boolean(subtitlesLanguage?.id),
            socials_friendly: socialsFriendly,
            overlays_to_burn: burnCta ? 'cta' : '',
        };

        if (subtitlesLanguage?.id && !isDownloadingAllResources) {
            params.subs_lang = subtitlesLanguage?.id;
        }

        VideoService.downloadStory(video.id, onSuccess, onError, 0, params, isDownloadingAllResources);
        intervalRef.current = setInterval(updatePercentage, 1000);
        setStatus('processing');
    };

    function generateMarkdownFromStoryMetadata(data) {
        let markdown = "";

        markdown += `# Video Brief for video ([Link](${process.env.DOMAIN}/engage/${video.id}))\n\n`;
        markdown += `### Links are valid for 60 minutes since download of this file\n\n`;
        markdown += `**Note:** All durations and offsets are in milliseconds.\n\n`;
        markdown += `**Note 2:** Volumes go from 0 to 1.25\n\n`;


        markdown += `## Intro (${data.intro ? "YES" : "NO"})\n\n${data.intro ? `[Link](${data.intro})` : ""}\n\n`;
        markdown += `## Outro (${data.outro ? "YES" : "NO"})\n\n${data.outro ? `[Link](${data.outro})` : ""}\n\n`;
        markdown += `## Logo (${data.logo ? "YES" : "NO"})\n\n${data.logo ? `[Link](${data.logo})` : ""}\n\n`;


        markdown += `## CTAs\n\n`;
        if (data.ctas && data.ctas.length > 0) {
            data.ctas.forEach(cta => {
                markdown += `- **Text:** ${cta.text}\n`;
                markdown += `  **Position:** ${cta.position}\n`;
                markdown += `  **Size:** ${cta.size}\n`;
                markdown += `  **Radius:** ${cta.radius}\n`;
                markdown += `  **Offset:** ${cta.offset}\n`;
                markdown += `  **Duration:** ${cta.duration}\n`;
                markdown += `  **Background Color:** ${cta.bgColor}\n`;
                markdown += `  **Text Color:** ${cta.textColor}\n\n`;
                markdown += `  **Image URL:** [Link](${cta.resourceUrl})\n\n`;
            });
        } else {
            markdown += "No CTAs found.\n\n"
        }

        markdown += `## Scenes\n\n`;
        data.scenes.sort((a, b) => a.sort - b.sort).forEach(scene => {
            markdown += `### ${scene.name}\n\n`;
            markdown += `**ID:** ${scene.id}\n\n`;
            markdown += `**Sort:** ${scene.sort}\n\n`;
            markdown += `**Script:** ${scene.script || "N/A"}\n\n`;
            markdown += `**Narrative:** ${scene.narrative || "N/A"}\n\n`;
            markdown += `**Normalized Video:** [Link](${scene.normalizedUrl})\n\n`;
            markdown += `**Trimmed Video:** ${scene.trimmedUrl ? `[Link](${scene.trimmedUrl})` : "N/A"}\n\n`;
            markdown += `**Trim Start:** ${scene.trimStart}\n\n`;
            markdown += `**Trim End:** ${scene.trimEnd}\n\n`;
            markdown += `**Playback Rate:** ${scene.playbackRate}x\n\n`;
            const getMusicTitleById = (id) => {
                const music = data.musicUrls.find(music => music.id === id);
                return music ? music.title : "N/A";
            };

            markdown += `**Music Title:** ${getMusicTitleById(scene.musicId)}\n\n`;
            markdown += `**Music Volume:** ${scene.musicVolume}\n\n`;
            markdown += `**Video Volume:** ${scene.videoVolume}\n\n`;
            markdown += `**Subtitles SRT:** [Link](${scene.subtitlesUrl})\n\n`;

            if (scene.textPanelText) {
                markdown += `**Text Panel Text:**\n\n`;
                markdown += `- Top: ${scene.textPanelText.textTop}\n`;
                markdown += `- Bottom: ${scene.textPanelText.textBottom}\n\n`;
                markdown += `- Image URL: [Link](${scene.textPanelText.imageUrl})\n\n`;
            }

            if (scene.zooms && scene.zooms.length > 0) {
                markdown += `**Zooms:**\n\n`;
                scene.zooms.forEach(zoom => {
                    markdown += `  - Scale: ${zoom.scale}x\n`;
                    markdown += `  - Offset: ${zoom.offset}\n`;
                    markdown += `  - Duration: ${zoom.duration}\n`;
                    markdown += `  - Margin X% (from top left corner): ${zoom.marginX}\n`;
                    markdown += `  - Margin Y% (from top left corner): ${zoom.marginY}\n`;
                    markdown += `  - Zoom Type: ${zoom.zoomType}\n\n`;
                });
            }
        });


        markdown += `## Text Panel Style\n\n`;
        for (let key in data.textPanelStyle) {
            if (key == 'id' || key == 'title' || key == 'active' || key == 'type') {
                continue;
            }
            const formattedKey = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase());
            if (typeof data.textPanelStyle[key] === 'object') {
                markdown += `**${formattedKey}:**\n`;
                for (let subKey in data.textPanelStyle[key]) {
                    const formattedSubKey = subKey.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase());
                    if (typeof data.textPanelStyle[key][subKey] === 'object') {
                        markdown += `- **${formattedSubKey}:**\n`;
                        for (let subSubKey in data.textPanelStyle[key][subKey]) {
                            if (subSubKey == 'id' || subSubKey == 'createdAt' || subSubKey == 'default') {
                                continue;
                            }
                            const formattedSubSubKey = subSubKey.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase());
                            if (subSubKey === 'url') {
                                markdown += `  - ${formattedSubSubKey}: [Link](${data.textPanelStyle[key][subKey][subSubKey]})\n`;
                            } else {
                                markdown += `  - ${formattedSubSubKey}: ${data.textPanelStyle[key][subKey][subSubKey]}\n`;
                            }
                        }
                    } else {
                        markdown += `- ${formattedSubKey}: ${data.textPanelStyle[key][subKey]}\n`;
                    }
                }
                markdown += '\n'; // Add a newline for better readability
            } else {
                if (formattedKey === 'Position') {
                    const positionMap = {
                        'bl': 'Bottom Left',
                        'br': 'Bottom Right',
                        'tl': 'Top Left',
                        'tr': 'Top Right',
                        'tc': 'Top Center',
                        'bc': 'Bottom Center'
                    };
                    markdown += `**${formattedKey}:** ${positionMap[data.textPanelStyle[key]] || data.textPanelStyle[key]}\n\n`;
                } else if (formattedKey === 'Margin X' || formattedKey === 'Margin Y') {
                    markdown += `**${formattedKey}% (from position):** ${data.textPanelStyle[key]}\n\n`;
                } else {
                    markdown += `**${formattedKey}:** ${data.textPanelStyle[key]}\n\n`;
                }
            }
        }

        markdown += `## Voiceovers\n\n`;
        markdown += `**Note:** Voiceovers span through the whole video, they are not per scene (Story Offset indicates the offset within the video with all scenes combined) .\n\n`;
        if (data.audioTrack && data.audioTrack.audioSegments) {
            const sortedSegments = data.audioTrack.audioSegments.sort((a, b) => a.storyOffset - b.storyOffset);
            sortedSegments.forEach((segment, index) => {
                markdown += `### Voiceover Segment ${index + 1}\n\n`;
                for (let key in segment) {
                    if (!["id", "audioTrackId", "createdAt", "updatedAt", "subtitles"].includes(key)) {
                        if (key === "path") {
                            markdown += `**${key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase())}:** [Link](${segment[key]})\n\n`;
                        } else {
                            markdown += `**${key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, c => c.toUpperCase())}:** ${segment[key]}\n\n`;
                        }
                    }
                }
                if (segment.subtitles && segment.subtitles.length > 0) {
                    const originalSubtitle = segment.subtitles.find(subtitle => subtitle.original);
                    if (originalSubtitle) {
                        markdown += `**Subtitles:**\n\n`;
                        markdown += `- **Language:** ${originalSubtitle.language} - \n`;
                        markdown += `  [Link](${originalSubtitle.srtPath})\n`;
                    }
                }
            });
        } else {
            markdown += "No voiceovers found.\n\n";
        }


        markdown += `## Music URLs\n\n`;
        data.musicUrls.forEach(music => {
            markdown += `- **${music.title}:** [Link](${music.url})\n`;
        });
        markdown += `\n`;

        return markdown;
    }

    const handleDownloadMetadata = useCallback(async (hasBeenDownloaded = false) => {
        if (!hasBeenDownloaded) {
            await onStart(true);
            return;
        }

        setStatus('processing');
        setPercentage(0);

        try {
            const data = await VormatsAPI.downloadStoryMetadata(video.id);
            const markdown = await generateMarkdownFromStoryMetadata(data);
            setPercentage(10);

            const metadataPdf = await VormatsAPI.markdownToPdf(markdown);
            setPercentage(20);

            const zip = new JSZip();
            const rootFolder = zip.folder(`${video.id}_resources`);
            rootFolder.file('video_brief.pdf', metadataPdf);

            try {
                const scenesFolder = rootFolder.folder('scenes');
                const musicFolder = rootFolder.folder('music');
                const fontsFolder = rootFolder.folder('fonts');
                const audioFolder = rootFolder.folder('audio');
                const overlaysFolder = rootFolder.folder('overlays');

                const fetchAndAddToZip = async (url, folder, customFilename) => {
                    if (!url) return;
                    const filename = customFilename || getFilename(url);
                    const response = await fetch(url);
                    const blob = await response.blob();
                    folder.file(filename, blob);
                };

                const getFilename = (url) => {
                    return url.split('?')[0].split('/').pop();
                }

                // Calculate total files to download for progress tracking
                const totalFiles = [
                    data.logo,
                    ...data.scenes.map(s => s.normalizedUrl),
                    ...data.scenes.map(s => s.trimmedUrl),
                    ...data.scenes.map(s => s.subtitlesUrl),
                    ...data.scenes.map(s => s.textPanelText?.imageUrl),
                    ...data.musicUrls.map(m => m.url),
                    data.textPanelStyle?.panelTop?.font?.url,
                    data.textPanelStyle?.panelBottom?.font?.url,
                    data.textPanelStyle?.labelTop?.font?.url,
                    data.textPanelStyle?.labelBottom?.font?.url,
                    ...(data.audioTrack?.audioSegments?.map(s => s.path) || []),
                    data.intro,
                    data.outro,
                    ...data.ctas.map(cta => cta.resourceUrl)
                ].filter(Boolean);

                const totalFileCount = totalFiles.length;
                let completedFiles = 0;

                // Process files in batches of 5 to avoid overwhelming the browser
                const batchSize = 5;
                for (let i = 0; i < totalFiles.length; i += batchSize) {
                    const batch = totalFiles.slice(i, i + batchSize);
                    await Promise.all(batch.map(async (url) => {
                        if (url === data.logo) {
                            await fetchAndAddToZip(url, rootFolder, `logo_${getFilename(url)}`);
                        } else if (data.scenes.some(s => s.normalizedUrl === url || s.trimmedUrl === url)) {
                            await fetchAndAddToZip(url, scenesFolder);
                        } else if (data.scenes.some(s => s.subtitlesUrl === url)) {
                            await fetchAndAddToZip(url, scenesFolder, `subtitles_${getFilename(url)}`);
                        } else if (data.musicUrls.some(m => m.url === url)) {
                            await fetchAndAddToZip(url, musicFolder);
                        } else if ([
                            data.textPanelStyle?.labelTop?.font?.url,
                            data.textPanelStyle?.labelBottom?.font?.url,
                            data.textPanelStyle?.panelTop?.font?.url,
                            data.textPanelStyle?.panelBottom?.font?.url
                        ].includes(url)) {
                            await fetchAndAddToZip(url, fontsFolder);
                        } else if (data.audioTrack?.audioSegments?.some(s => s.path === url)) {
                            await fetchAndAddToZip(url, audioFolder);
                        } else if (url === data.intro) {
                            await fetchAndAddToZip(url, rootFolder, `intro_${getFilename(url)}`);
                        } else if (url === data.outro) {
                            await fetchAndAddToZip(url, rootFolder, `outro_${getFilename(url)}`);
                        } else if (data.scenes.some(s => s.textPanelText?.imageUrl === url)) {
                            await fetchAndAddToZip(url, overlaysFolder);
                        } else if (data.ctas.some(cta => cta.resourceUrl === url)) {
                            await fetchAndAddToZip(url, overlaysFolder, `cta_${getFilename(url)}`);
                        }

                        completedFiles++;
                        const downloadProgress = Math.round((completedFiles / totalFileCount) * 70);
                        setPercentage(Math.round(20 + downloadProgress));
                    }));
                }

                setPercentage(90);

                const zipBlob = await zip.generateAsync({ type: 'blob' });

                setPercentage(100);

                const downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(zipBlob);
                downloadLink.download = `${video.id}_resources.zip`;
                downloadLink.click();
                URL.revokeObjectURL(downloadLink.href);
                setStatus('done');
                callback && callback();
                setTimeout(() => {
                    setStatus('pending');
                    setPercentage(0);
                    setDownloadLink(null);
                })
            } catch (error) {
                console.error('Error creating zip file:', error);
                ToastNotification({
                    iconName: 'failed',
                    message: t('download_modal.download_all_resources.failed'),
                });
                setStatus('error');
            }
        } catch (error) {
            console.error('Error downloading metadata:', error);
            ToastNotification({
                iconName: 'failed',
                message: t('download_modal.download_all_resources.failed'),
            });
            setStatus('error');
        }
    }, [video, generateMarkdownFromStoryMetadata, callback]);

    useEffect(() => {
        if (video) {
            const hasSubtitles = video?.subtitlesMeta?.length > 0;

            if ('duration' in video && estimatedTime === 0) {
                let videoDurationSeconds = video.duration / 1000;
                let multiplier = 0.4;
                if (hasSubtitles) {
                    multiplier = videoDurationSeconds < 45 ? 2 : 1.2;
                }
                setEstimatedTime(videoDurationSeconds * multiplier);
            }
        }
    }, [video]);

    useEffect(() => {
        clearInterval(intervalRef.current);
    }, []);

    return { onStart, handleDownloadMetadata, percentage, status, downloadLink };
}

export default useDownloadVideo;